import React from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Briefcase, Settings, Search, Home, FileText, Users, FolderOpen, FileCheck, Mail, Monitor, Target, TrendingUp, Globe, Building2 } from 'lucide-react';

const iconMap = {
  briefcase: Briefcase,
  settings: Settings,
  search: Search,
  home: Home,
  fileText: FileText,
  users: Users,
  folderOpen: FolderOpen,
  fileCheck: FileCheck,
  mail: Mail,
  monitor: Monitor,
  target: Target,
  trendingUp: TrendingUp,
  globe: Globe,
  building: Building2
};

export const Services = ({ t, serviceType, onServiceTypeChange }) => {
  const services = serviceType === 'professional' ? t.services.b2b : t.services.b2c;

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#D4AF37]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-100/50 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#001f3f] mb-4">
            {t.services.title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t.services.subtitle}
          </p>
        </div>

        {/* Service Type Toggle */}
        <div className="flex justify-center mb-12">
          <div className="inline-flex bg-white rounded-full p-2 shadow-lg border border-slate-200">
            <Button
              onClick={() => onServiceTypeChange('professional')}
              className={`rounded-full px-8 py-3 transition-all duration-300 ${
                serviceType === 'professional'
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#001f3f] shadow-md'
                  : 'bg-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t.serviceToggle.professional}
            </Button>
            <Button
              onClick={() => onServiceTypeChange('individual')}
              className={`rounded-full px-8 py-3 transition-all duration-300 ${
                serviceType === 'individual'
                  ? 'bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#001f3f] shadow-md'
                  : 'bg-transparent text-slate-600 hover:bg-slate-100'
              }`}
            >
              {t.serviceToggle.individual}
            </Button>
          </div>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card 
                key={`service-${service.icon}-${index}`}
                className={`group relative bg-white/80 backdrop-blur-sm border-slate-200 hover:border-[#D4AF37] transition-all duration-500 overflow-hidden hover:shadow-2xl hover:-translate-y-2 ${
                  service.highlight ? 'ring-2 ring-[#D4AF37]/30' : ''
                }`}
              >
                {/* Highlight badge for featured services */}
                {service.highlight && (
                  <div className="absolute top-4 right-4 z-20">
                    <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#001f3f] font-semibold border-0 shadow-lg">
                      {serviceType === 'individual' ? '50% Crédit' : 'Premium'}
                    </Badge>
                  </div>
                )}

                {/* Glassmorphism overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/0 to-[#D4AF37]/0 group-hover:from-[#D4AF37]/10 group-hover:to-[#FFD700]/5 transition-all duration-500"></div>
                
                <div className="p-8 relative z-10">
                  {/* Icon - More interactive */}
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#001f3f] to-[#003366] flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg group-hover:shadow-2xl group-hover:shadow-[#D4AF37]/30">
                    <Icon className="w-8 h-8 text-[#D4AF37] group-hover:scale-125 transition-transform duration-500" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-[#001f3f] mb-4 group-hover:text-[#D4AF37] transition-colors duration-300 leading-tight">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed text-sm">
                    {service.description}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
